import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { DatosProducto } from '../../../venta/presentation/components/EnvioNacionalModal/DatosProducto';
import {
  DatosProducto as DatosProductoType,
  DatosRemitente,
  EnvioNacionalForm,
  getEnvioNacionalFormInitial,
} from '../../../venta/presentation/components/EnvioNacionalModal/EnvioNacionalModal.types';
import { filtrarCiudadesCO } from '../../../venta/presentation/components/EnvioNacionalModal/EnvioNacionalModal.utils';
import { CiudadCO } from '../../../venta/presentation/components/EnvioNacionalModal/PersonaFormColombia';
import { VentaApiService } from '../../../venta/infrastructure/api/VentaApiService';
import { useSession } from '../../../../core/context/SessionContext';
import { isLigeroListoParaCotizar, toWizardInitialForm } from './CotizacionLigeroModal.utils';

interface Props {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  onCrearGuia: (form: EnvioNacionalForm) => void;
}

export const CotizacionLigeroModal: React.FC<Props> = ({
  open, onClose, accessToken, onCrearGuia,
}) => {
  const { terminal } = useSession();
  const pais = terminal?.pais ?? '';
  const esColombia = pais === 'CO';

  const [form, setForm] = useState<EnvioNacionalForm>(getEnvioNacionalFormInitial(pais));
  const [ciudadesCO, setCiudadesCO] = useState<CiudadCO[]>([]);
  const [isLoadingCiudadesCO, setIsLoadingCiudadesCO] = useState(false);
  const [isLoadingCPOrigen, setIsLoadingCPOrigen] = useState(false);
  const [isLoadingCPDestino, setIsLoadingCPDestino] = useState(false);
  const [errorCPOrigen, setErrorCPOrigen] = useState<string | null>(null);
  const [errorCPDestino, setErrorCPDestino] = useState<string | null>(null);
  const [precioCotizado, setPrecioCotizado] = useState<number | null>(null);
  const [isLoadingCotizacion, setIsLoadingCotizacion] = useState(false);
  const [errorCotizacion, setErrorCotizacion] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(getEnvioNacionalFormInitial(pais));
    setPrecioCotizado(null);
    setErrorCotizacion(null);
    setErrorCPOrigen(null);
    setErrorCPDestino(null);
  }, [open, pais]);

  useEffect(() => {
    if (!open || !esColombia) return;
    setIsLoadingCiudadesCO(true);
    VentaApiService.obtenerCiudadesCO(accessToken)
      .then(setCiudadesCO)
      .catch(() => setCiudadesCO([]))
      .finally(() => setIsLoadingCiudadesCO(false));
  }, [open, esColombia, accessToken]);

  const resetFormulario = () => {
    setForm(getEnvioNacionalFormInitial(pais));
    setPrecioCotizado(null);
    setErrorCotizacion(null);
    setErrorCPOrigen(null);
    setErrorCPDestino(null);
  };

  const handleCiudadSelect = (lado: 'remitente' | 'destinatario', ciudad: CiudadCO) => {
    setForm(prev => ({
      ...prev,
      [lado]: {
        ...prev[lado],
        ciudadId: ciudad.id,
        ciudadLabel: ciudad.label,
        municipio: ciudad.label,
        codigoCm: ciudad.codigoCm,
      },
    }));
    setPrecioCotizado(null);
    setErrorCotizacion(null);
  };

  const handleCodigoPostal = async (lado: 'remitente' | 'destinatario', value: string) => {
    const setLoading = lado === 'remitente' ? setIsLoadingCPOrigen : setIsLoadingCPDestino;
    const setError = lado === 'remitente' ? setErrorCPOrigen : setErrorCPDestino;
    setForm(prev => ({
      ...prev,
      [lado]: { ...prev[lado], codigoPostal: value, municipio: '', codigoCm: '', codigoCiudadDane: '' },
    }));
    setError(null);
    setPrecioCotizado(null);
    setErrorCotizacion(null);
    if (value.length !== 5) return;
    setLoading(true);
    try {
      const res = await VentaApiService.consultarCodigoPostal(value, accessToken, pais);
      setForm(prev => ({
        ...prev,
        [lado]: {
          ...prev[lado],
          municipio: res.ciudad,
          codigoCm: res.codigoCm,
          codigoCiudadDane: res.codigoCiudadDane,
        },
      }));
    } catch {
      setError('Código postal no válido');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeProducto = (field: keyof DatosProductoType, value: unknown) => {
    setForm(prev => ({ ...prev, producto: { ...prev.producto, [field]: value } }));
    setPrecioCotizado(null);
    setErrorCotizacion(null);
  };

  const handleCotizar = async () => {
    if (!isLigeroListoParaCotizar(form, esColombia)) return;
    setIsLoadingCotizacion(true);
    setErrorCotizacion(null);
    setPrecioCotizado(null);
    try {
      const precio = await VentaApiService.cotizarEnvioNacional(form, accessToken, pais);
      setPrecioCotizado(precio);
    } catch (error) {
      setErrorCotizacion(error instanceof Error ? error.message : 'Error al cotizar el envío');
    } finally {
      setIsLoadingCotizacion(false);
    }
  };

  const handleCrearGuia = () => {
    if (precioCotizado === null || precioCotizado <= 0) return;
    onCrearGuia(toWizardInitialForm(form, pais));
  };

  const puedeCotizar = isLigeroListoParaCotizar(form, esColombia) && !isLoadingCotizacion;
  const puedeCrearGuia = !isLoadingCotizacion && precioCotizado !== null && precioCotizado > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="cotizacion-ligero-modal">
      <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RequestQuoteIcon color="primary" />
          Cotizar envío nacional
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase' }}>
          Origen y destino
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {esColombia ? (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CiudadAutocomplete
                  label="Ciudad origen"
                  datos={form.remitente}
                  ciudades={ciudadesCO}
                  loading={isLoadingCiudadesCO}
                  onSelect={ciudad => handleCiudadSelect('remitente', ciudad)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CiudadAutocomplete
                  label="Ciudad destino"
                  datos={form.destinatario}
                  ciudades={ciudadesCO}
                  loading={isLoadingCiudadesCO}
                  onSelect={ciudad => handleCiudadSelect('destinatario', ciudad)}
                />
              </Grid>
            </>
          ) : (
            <>
              <CodigoPostalFields
                lado="origen"
                codigoPostal={form.remitente.codigoPostal}
                municipio={form.remitente.municipio}
                loading={isLoadingCPOrigen}
                error={errorCPOrigen}
                onChange={value => handleCodigoPostal('remitente', value)}
              />
              <CodigoPostalFields
                lado="destino"
                codigoPostal={form.destinatario.codigoPostal}
                municipio={form.destinatario.municipio}
                loading={isLoadingCPDestino}
                error={errorCPDestino}
                onChange={value => handleCodigoPostal('destinatario', value)}
              />
            </>
          )}
        </Grid>

        <DatosProducto
          datos={form.producto}
          onChange={handleChangeProducto}
          esColombia={esColombia}
          modoCotizador
        />

        {isLoadingCotizacion && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Calculando precio del envío...</Typography>
          </Box>
        )}
        {errorCotizacion && !isLoadingCotizacion && (
          <Alert severity="warning" sx={{ mt: 2 }}>{errorCotizacion}</Alert>
        )}
        {precioCotizado !== null && precioCotizado > 0 && !isLoadingCotizacion && (
          <Box
            data-testid="precio-cotizado"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 2, mt: 2, borderRadius: 2,
              backgroundColor: 'primary.main', color: 'white',
            }}
          >
            <LocalShippingIcon fontSize="small" />
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Precio estimado del envío
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ${precioCotizado.toLocaleString(esColombia ? 'es-CO' : 'es-MX')}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f9f9f9', gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={resetFormulario} color="inherit" sx={{ fontWeight: 'bold' }}>
          Reestablecer
        </Button>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 'bold' }}>
          Cancelar
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleCotizar}
          variant="outlined"
          disabled={!puedeCotizar}
          sx={{ fontWeight: 'bold', borderRadius: 8, px: 3 }}
        >
          Cotizar
        </Button>
        <Button
          onClick={handleCrearGuia}
          variant="contained"
          disabled={!puedeCrearGuia}
          sx={{ fontWeight: 'bold', borderRadius: 8, px: 3 }}
        >
          Crear Guía
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CiudadAutocompleteProps {
  label: string;
  datos: DatosRemitente;
  ciudades: CiudadCO[];
  loading: boolean;
  onSelect: (ciudad: CiudadCO) => void;
}

const CiudadAutocomplete: React.FC<CiudadAutocompleteProps> = ({
  label, datos, ciudades, loading, onSelect,
}) => {
  const selected = ciudades.find(c => c.id === datos.ciudadId) ?? null;
  const [query, setQuery] = useState(datos.ciudadLabel ?? '');

  useEffect(() => {
    setQuery(datos.ciudadLabel ?? '');
  }, [datos.ciudadLabel]);

  return (
    <Autocomplete
      options={ciudades}
      value={selected}
      inputValue={query}
      loading={loading}
      onInputChange={(_, value) => setQuery(value)}
      onChange={(_, ciudad) => {
        if (ciudad) {
          onSelect(ciudad);
          setQuery(ciudad.label);
        }
      }}
      getOptionLabel={c => c.label}
      getOptionKey={c => c.id}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      filterOptions={(opts, { inputValue }) => filtrarCiudadesCO(opts, inputValue)}
      autoHighlight
      renderInput={params => (
        <TextField {...params} required size="small" label={label} />
      )}
      noOptionsText="Sin resultados"
    />
  );
};

interface CodigoPostalFieldsProps {
  lado: 'origen' | 'destino';
  codigoPostal: string;
  municipio: string;
  loading: boolean;
  error: string | null;
  onChange: (value: string) => void;
}

const CodigoPostalFields: React.FC<CodigoPostalFieldsProps> = ({
  lado, codigoPostal, municipio, loading, error, onChange,
}) => (
  <>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField
        fullWidth
        required
        size="small"
        label={`Código postal ${lado}`}
        value={codigoPostal}
        onChange={e => onChange(e.target.value.replaceAll(/\D/g, '').slice(0, 5))}
        error={Boolean(error)}
        helperText={error ?? undefined}
        inputMode="numeric"
        slotProps={{
          input: { endAdornment: loading ? <CircularProgress size={16} /> : null },
        }}
      />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField
        fullWidth
        size="small"
        label={`Municipio ${lado}`}
        value={municipio}
        disabled
      />
    </Grid>
  </>
);
